import { StrictMode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from './layouts/MainLayout';
import { AuthGuard } from './components/auth/AuthGuard';
import { SignInForm } from './components/auth/SignInForm';
import { SignUpForm } from './components/auth/SignUpForm';
import { CeoDashboard } from './components/dashboard/ceo/CeoDashboard';
import { CfoDashboard } from './components/dashboard/cfo/CfoDashboard';
import { ManagerDashboard } from './components/manager/ManagerDashboard';
import { ExecutiveTasksPage } from './pages/ExecutiveTasksPage';
import { EmployeeDashboard } from './components/employee/EmployeeDashboard';
import { DepartmentsPage } from './pages/DepartmentsPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { SettingsPage } from './pages/SettingsPage';
import { AdminPanel } from './pages/AdminPanel';
import { TasksPage } from './pages/TasksPage';
import { Toaster } from 'react-hot-toast';
import { useStore } from './store/useStore';
import { useAuth } from './hooks/auth/useAuth';
import { useRealtimeSync } from './hooks/database/useRealtimeSync';
import { useTaskSync } from './hooks/database/useTaskSync';
import { initSupabase } from './config/supabase';

// Initialize Supabase on app start
initSupabase().catch(console.error);

function App() {
  const { currentUser } = useStore();
  
  // Initialize auth
  useAuth();

  // Initialize real-time sync
  useRealtimeSync();
  useTaskSync();

  const getDashboard = () => {
    if (!currentUser) return null;
    
    switch (currentUser.role) {
      case 'CEO':
        return <CeoDashboard key={currentUser.id} />;
      case 'CFO':
        return <CfoDashboard key={currentUser.id} />;
      case 'MANAGER':
        return <ManagerDashboard />;
      default:
        return <EmployeeDashboard />;
    }
  };

  return (
    <Router>
      <Toaster position="top-right" />
      <Routes>
        {/* Public Routes */}
        <Route path="/signin" element={<SignInForm />} />
        <Route path="/signup" element={<SignUpForm />} />

        {/* Protected Routes */}
        <Route element={<MainLayout />}>
          <Route path="/" element={
            <AuthGuard>{getDashboard()}</AuthGuard>
          } />
          
          <Route path="/departments" element={
            <AuthGuard><DepartmentsPage /></AuthGuard>
          } />
          
          <Route path="/my-tasks" element={
            <AuthGuard><TasksPage /></AuthGuard>
          } />
          
          <Route path="/tasks" element={
            <AuthGuard><ExecutiveTasksPage /></AuthGuard>
          } />
          
          {/* Executive Routes */}
          <Route path="/analytics" element={
            <AuthGuard requiredRole={['CEO', 'CFO']}><AnalyticsPage /></AuthGuard>
          } />
          
          <Route path="/admin" element={
            <AuthGuard requiredRole={['CEO', 'CFO']}><AdminPanel /></AuthGuard>
          } />
          
          <Route path="/settings" element={
            <AuthGuard><SettingsPage /></AuthGuard>
          } />
        </Route>

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;